import { TITLE_PENDING } from "@repo/agent-chat";
import type { VisitDto } from "@repo/data-store";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@repo/ui/components/ui/breadcrumb";
import { SidebarTrigger } from "@repo/ui/components/ui/sidebar";
import { Link } from "@tanstack/react-router";
import { Fragment, type ReactNode } from "react";
import { catalogCrumbs } from "@/app/shell/catalog-crumbs";
import type { BreadcrumbData } from "@/app/shell/use-breadcrumb-data";
import { PractitionerDisplay } from "@/shared/common/practitioner-display";
import { formatDate } from "@/shared/lib/date-formats";
import { ActiveSessionsIndicator } from "./active-sessions-indicator";
import { NotificationBell } from "./notification-bell";

function toTitleCase(str: string) {
  return str.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function CatalogBreadcrumb({ data }: { data: BreadcrumbData }) {
  const crumbs = catalogCrumbs(data.pathnames, data.catalogTitle);
  return (
    <Breadcrumb>
      <BreadcrumbList>
        {crumbs.map((crumb, index) => (
          <Fragment key={`${index}-${crumb.label}`}>
            {index > 0 && <BreadcrumbSeparator className="hidden md:block" />}
            <BreadcrumbItem>
              {crumb.to ? (
                <BreadcrumbLink
                  render={<Link to={crumb.to} search={crumb.group ? { group: crumb.group } : undefined} />}
                >
                  {crumb.label}
                </BreadcrumbLink>
              ) : (
                <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
              )}
            </BreadcrumbItem>
          </Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

function AppBreadcrumb({ data }: { data: BreadcrumbData }) {
  if (data.pathnames[0] === "catalog") return <CatalogBreadcrumb data={data} />;
  const {
    pathnames,
    patientId,
    scheduleId,
    visitId,
    caseId,
    carePlanId,
    documentId,
    documentTitle,
    taskId,
    assessmentKindSegment,
    assessmentId,
    assessmentTitle,
    chatThreadSegment,
    chatThreadTitle,
    patientName,
    scheduleActorInfo,
    visitDate,
    caseName,
    carePlanLabel,
    taskBreadcrumb,
  } = data;

  const scheduleActorName = scheduleActorInfo?.name;

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {pathnames.map((value, index) => {
          const to = `/${pathnames.slice(0, index + 1).join("/")}`;
          const isLast = index === pathnames.length - 1;

          // `q`/`e` only tells the router which id follows; it addresses no page.
          if (assessmentKindSegment && index > 0 && pathnames[index - 1] === "assessments") return null;

          const isPatientId = patientId && value === patientId;
          const isScheduleId = scheduleId && value === scheduleId;
          const isVisitId = visitId && value === visitId;
          const isCaseId = caseId && value === caseId;
          const isCarePlanId = carePlanId && value === carePlanId;
          const isDocumentId = documentId && value === documentId;
          const isTaskId = taskId && value === taskId;
          const isChatThreadId = chatThreadSegment && value === chatThreadSegment;

          let name: ReactNode = toTitleCase(value);
          if (isChatThreadId) {
            // A thread is named by its generated title, never its id. Until the
            // title exists, shimmer the same placeholder the sessions rail and
            // palette history show — one state, one appearance.
            name = chatThreadTitle ?? <span className="shimmer truncate text-muted-foreground">{TITLE_PENDING}</span>;
          } else if (isPatientId && patientName) {
            name = patientName;
          } else if (isScheduleId && scheduleActorInfo) {
            if (scheduleActorInfo.type === "Practitioner" && scheduleActorInfo.ref) {
              name = (
                <PractitionerDisplay
                  practitionerId={scheduleActorInfo.ref.split("/")[1]}
                  fallbackDisplay={scheduleActorName}
                />
              );
            } else if (scheduleActorName) {
              name = scheduleActorName;
            }
          } else if (isVisitId && visitDate) {
            name = formatDate(visitDate);
          } else if (isCaseId && caseName) {
            name = caseName;
          } else if (isCarePlanId && carePlanLabel) {
            name = carePlanLabel;
          } else if (isDocumentId) {
            name = documentTitle ?? "Document";
          } else if (isTaskId && taskBreadcrumb) {
            name = taskBreadcrumb;
          } else if (assessmentId && value === assessmentId) {
            name = assessmentTitle ?? "Assessment";
          }

          const linkRender = <Link to={to} />;

          return (
            <Fragment key={to}>
              {index > 0 && <BreadcrumbSeparator className="hidden md:block" />}
              <BreadcrumbItem>
                {isLast ? (
                  <BreadcrumbPage>{name}</BreadcrumbPage>
                ) : (
                  <BreadcrumbLink render={linkRender}>{name}</BreadcrumbLink>
                )}
              </BreadcrumbItem>
            </Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

interface AppHeaderProps {
  breadcrumbData: BreadcrumbData;
  inProgressVisits: VisitDto[];
}

export function AppHeader({ breadcrumbData, inProgressVisits }: AppHeaderProps) {
  return (
    <header className="bg-background sticky top-0 flex h-14 shrink-0 items-center gap-2 border-b px-4">
      <SidebarTrigger className="-ml-1" />
      <AppBreadcrumb data={breadcrumbData} />
      <div className="ml-auto flex items-center gap-2">
        <NotificationBell />
        <ActiveSessionsIndicator visits={inProgressVisits} />
      </div>
    </header>
  );
}
